// Zustand store for the Daten-Bot widget's client-only UI state: panel
// open/closed, which conversation is active, remembered model selection,
// and the async-completion tracking (pending turns + unseen-answer badge).
// Server state (conversations/turns/answers/settings) lives in react-query
// (see hooks/useBot.ts) — this store never holds fetched data.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Which shell renders the widget's content. 'popover' = single-column chat
 *  anchored bottom-right (BotPanel, the P2 default). 'dock' = right-docked
 *  multi-model compare panel (BotCompareDock, P3). Persisted — Design Spec
 *  §9 / §13.6: "docked right panel"; "toggle back to popover any time;
 *  state persists." */
export type BotViewMode = 'popover' | 'dock'

/** Which pane is shown inside whichever shell (popover or dock) is open. */
export type BotTab = 'chat' | 'memory' | 'settings' | 'report'

interface BotState {
  /** Whether the popover is open. Never persisted as true — a reload always
   *  starts closed, even if the panel was open at save time. */
  isOpen: boolean
  /** Conversation currently shown in the panel; null = "new conversation". */
  activeConversationId: number | null
  /** Last model selection in the composer, remembered across turns. */
  selectedModels: string[]
  /** Turn ids with at least one non-terminal answer, being watched for
   *  completion (see BotWidget's <TurnWatcher/> children). */
  pendingTurnIds: number[]
  /** Count of finished answers the user hasn't seen yet (drives the launcher badge). */
  unseenCount: number
  /** Answer ids already shown to the user. Deliberately never capped/evicted
   *  — it only stores small numeric ids, so unbounded growth stays cheap in
   *  localStorage over realistic usage, and evicting old entries would make
   *  `catchUpSeen` mistake a long-ago-seen (now-evicted) answer for a
   *  genuinely new one, wrongly decrementing `unseenCount` a second time. */
  seenAnswerIds: number[]
  /** 'popover' (default) or 'dock' — see `BotViewMode`. Persisted so a
   *  compare session picked up mid-conversation stays docked across a reload. */
  viewMode: BotViewMode
  /** True once `viewMode` has been set at least once on this device/browser
   *  — either by an explicit user/auto action (`setViewMode`) or by the
   *  one-time server-settings seed (`seedViewMode`). Persisted alongside
   *  `viewMode` so the seed only ever runs once per browser: after that,
   *  `bot.UserSetting.compare_layout` is a *default* for a brand-new
   *  session/device, never a live override of whatever the user (or an
   *  earlier auto-switch) already set locally. */
  viewModeSeeded: boolean
  /** 'chat' (default), 'memory', 'settings', or 'report'. Never persisted — a fresh
   *  page load always starts back on Chat. Switching shells (popover<->dock)
   *  while the widget stays open does NOT reset it: `setViewMode` only
   *  touches `viewMode`, so whichever tab was active carries over to the
   *  shell being switched to. */
  activeTab: BotTab
  /** Composer draft text. Shared across shells so toggling popover<->dock
   *  (which unmounts/remounts BotPanel/BotCompareDock — mutually exclusive
   *  components, see BotWidget) never discards unsent input. Never
   *  persisted — same treatment as `activeTab`. */
  draft: string

  open: () => void
  close: () => void
  toggle: () => void
  setConversation: (id: number | null) => void
  setSelectedModels: (models: string[]) => void
  addPending: (turnId: number) => void
  removePending: (turnId: number) => void
  markSeen: (ids: number[]) => void
  /** Like `markSeen`, but also decrements `unseenCount` by however many of
   *  `ids` were not yet in `seenAnswerIds` — used when catching up on
   *  answers that were bumped as unseen while a different conversation (or
   *  no conversation) was being viewed. Never used from the live-view path
   *  (TurnWatcher's `markSeen` call), which never bumped in the first
   *  place, so plain `markSeen` there stays a no-op on the count. */
  catchUpSeen: (ids: number[]) => void
  bumpUnseen: (n: number) => void
  clearUnseen: () => void
  setViewMode: (mode: BotViewMode) => void
  /** One-time seed of `viewMode` from the server-side `compareLayout`
   *  setting — a no-op once `viewModeSeeded` is already true (see that
   *  field's own comment). Distinct from `setViewMode`, which always wins
   *  and always marks seeded, so a fresh-device seed can never race ahead
   *  of (or clobber) an explicit choice. */
  seedViewMode: (mode: BotViewMode) => void
  setActiveTab: (tab: BotTab) => void
  setDraft: (draft: string) => void
}

export const useBotStore = create<BotState>()(
  persist(
    (set) => ({
      isOpen: false,
      activeConversationId: null,
      selectedModels: [],
      pendingTurnIds: [],
      unseenCount: 0,
      seenAnswerIds: [],
      viewMode: 'popover',
      viewModeSeeded: false,
      activeTab: 'chat',
      draft: '',

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),

      setConversation: (id) => set({ activeConversationId: id }),
      setSelectedModels: (models) => set({ selectedModels: models }),

      addPending: (turnId) =>
        set((s) => (s.pendingTurnIds.includes(turnId) ? s : { pendingTurnIds: [...s.pendingTurnIds, turnId] })),
      removePending: (turnId) =>
        set((s) => ({ pendingTurnIds: s.pendingTurnIds.filter((id) => id !== turnId) })),

      markSeen: (ids) =>
        set((s) => {
          const toAdd = ids.filter((id) => !s.seenAnswerIds.includes(id))
          if (toAdd.length === 0) return s
          return { seenAnswerIds: [...s.seenAnswerIds, ...toAdd] }
        }),
      catchUpSeen: (ids) =>
        set((s) => {
          const toAdd = ids.filter((id) => !s.seenAnswerIds.includes(id))
          if (toAdd.length === 0) return s
          return { seenAnswerIds: [...s.seenAnswerIds, ...toAdd], unseenCount: Math.max(0, s.unseenCount - toAdd.length) }
        }),
      bumpUnseen: (n) => set((s) => ({ unseenCount: s.unseenCount + n })),
      clearUnseen: () => set({ unseenCount: 0 }),

      setViewMode: (mode) => set({ viewMode: mode, viewModeSeeded: true }),
      seedViewMode: (mode) => set((s) => (s.viewModeSeeded ? s : { viewMode: mode, viewModeSeeded: true })),
      setActiveTab: (tab) => set({ activeTab: tab }),
      setDraft: (draft) => set({ draft }),
    }),
    {
      name: 'mc-bot',
      // Design Spec §9: "Unread badge = count of finished-unseen answers
      // (zustand botStore, persisted)" — unseenCount (and the seenAnswerIds
      // it's computed against, already capped above) survive a reload too.
      // `isOpen` is deliberately excluded (not just defaulted) so a saved
      // "open" state can never rehydrate as true.
      partialize: (s) => ({
        activeConversationId: s.activeConversationId,
        selectedModels: s.selectedModels,
        unseenCount: s.unseenCount,
        seenAnswerIds: s.seenAnswerIds,
        viewMode: s.viewMode,
        viewModeSeeded: s.viewModeSeeded,
      }),
    },
  ),
)
